import React, { useState, useMemo, useRef, useEffect } from 'react';

/**
 * MarketHeatmap — TradingView-style treemap using the squarified algorithm
 * (Bruls, Huijse, van Wijk 2000). Cell areas are exactly proportional to
 * the item's weight (market cap). Sectors are laid out as treemap regions,
 * then each sector's items are laid out as a sub-treemap inside.
 */
export default function MarketHeatmap({
  items, quotes, loading,
  groupKey = 'sector', groups = [],
  onSelect,
}) {
  const [activeGroup, setActiveGroup] = useState('all');
  const [hovered, setHovered] = useState(null);
  const containerRef = useRef(null);
  const [size, setSize] = useState({ w: 1200, h: 580 });

  // Track container size for responsive layout
  useEffect(() => {
    if (!containerRef.current) return;
    const update = () => {
      const rect = containerRef.current.getBoundingClientRect();
      // Fit treemap to remaining viewport space, but cap height so the chart
      // never exceeds a comfortable on-screen size — even with lots of
      // headroom above. min 480, max 620. Keeps Stocks + Crypto identical.
      const headerOffset = rect.top + 60;
      const availableH = Math.min(620, Math.max(480, window.innerHeight - headerOffset));
      setSize({ w: rect.width, h: availableH });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(containerRef.current);
    window.addEventListener('resize', update);
    return () => { ro.disconnect(); window.removeEventListener('resize', update); };
  }, []);

  // Filter and group items.
  // Once quotes have loaded, drop any item that didn't get a valid quote —
  // CoinGecko's "top 200" includes tokens (stablecoins, RWA, brand new coins)
  // that Yahoo Finance simply doesn't serve, so they'd otherwise appear as
  // empty grey cells that 404 when clicked.
  const visibleItems = useMemo(() => {
    let filtered = items;
    if (!loading) {
      filtered = filtered.filter(i => {
        const q = quotes[i.ticker];
        return q && typeof q.currentPrice === 'number' && q.currentPrice > 0;
      });
    }
    if (activeGroup === 'all') return filtered;
    return filtered.filter(i => i[groupKey] === activeGroup);
  }, [items, quotes, loading, activeGroup, groupKey]);

  // Build sector groups
  const groupedTree = useMemo(() => {
    const map = new Map();
    visibleItems.forEach(item => {
      const g = item[groupKey] || 'Other';
      if (!map.has(g)) map.set(g, []);
      map.get(g).push(item);
    });
    const arr = [];
    for (const [name, list] of map.entries()) {
      list.sort((a, b) => (b.weight || 0) - (a.weight || 0));
      const total = list.reduce((s, i) => s + (i.weight || 1), 0);
      arr.push({ name, items: list, weight: total });
    }
    arr.sort((a, b) => b.weight - a.weight);
    return arr;
  }, [visibleItems, groupKey]);

  // Compute treemap layout: sectors first, then items within each sector
  const layout = useMemo(() => {
    if (!size.w || !size.h) return [];
    // For single sector: skip sector header, give items the full area
    if (groupedTree.length === 1 || activeGroup !== 'all') {
      const onlyGroup = groupedTree[0] || { items: [], name: '' };
      const tiles = squarify(onlyGroup.items, 0, 0, size.w, size.h);
      return [{ name: onlyGroup.name, x: 0, y: 0, w: size.w, h: size.h, tiles, showHeader: false }];
    }
    // Multi-sector: each sector reserves a small header strip (16px)
    const sectorRects = squarify(groupedTree, 0, 0, size.w, size.h);
    return sectorRects.map(rect => {
      const HEADER = 16;
      const innerY = rect.y + HEADER;
      const innerH = Math.max(0, rect.h - HEADER);
      const tiles = squarify(rect.items, rect.x, innerY, rect.w, innerH);
      return { ...rect, tiles, showHeader: true };
    });
  }, [groupedTree, size, activeGroup]);

  return (
    <div>
      {/* Filter chips */}
      <div style={s.filterBar}>
        <FilterChip active={activeGroup === 'all'} onClick={() => setActiveGroup('all')}>
          All
        </FilterChip>
        {groups.map(g => {
          const count = items.filter(i => i[groupKey] === g).length;
          if (!count) return null;
          return (
            <FilterChip key={g} active={activeGroup === g} onClick={() => setActiveGroup(g)}>
              {g}
            </FilterChip>
          );
        })}
      </div>

      {/* Treemap canvas */}
      <div ref={containerRef} style={{ ...s.canvas, height: `${size.h}px` }}>
        {layout.map(sector => (
          <React.Fragment key={sector.name}>
            {sector.showHeader && (
              <div
                style={{
                  position: 'absolute',
                  left: sector.x, top: sector.y,
                  width: sector.w, height: 16,
                  fontSize: '10px', fontWeight: 700, color: 'var(--text-secondary)',
                  textTransform: 'uppercase', letterSpacing: '0.5px',
                  paddingLeft: '4px', display: 'flex', alignItems: 'center',
                  overflow: 'hidden', whiteSpace: 'nowrap',
                  pointerEvents: 'none',
                }}
              >
                {sector.name}
              </div>
            )}
            {sector.tiles.map(tile => (
              <Cell
                key={tile.ticker}
                tile={tile}
                quote={quotes[tile.ticker]}
                loading={loading}
                isHovered={hovered === tile.ticker}
                onHover={setHovered}
                onClick={() => onSelect(tile.ticker)}
              />
            ))}
          </React.Fragment>
        ))}
      </div>

      {/* Legend */}
      <div style={s.legend}>
        <span style={s.legendLabel}>Daily change:</span>
        {[
          { c: '#7f1d1d', l: '< -3%' },
          { c: '#b91c1c', l: '-3 to -1%' },
          { c: '#475569', l: '±0.3%' },
          { c: '#16a34a', l: '+1 to +3%' },
          { c: '#14532d', l: '> +3%' },
        ].map(x => (
          <span key={x.l} style={s.legendItem}>
            <span style={{ ...s.legendSwatch, background: x.c }} />
            {x.l}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ── Squarified treemap algorithm ───────────────────────────── */
function squarify(items, x, y, width, height) {
  if (!items.length || width <= 0 || height <= 0) return [];
  const totalWeight = items.reduce((s, i) => s + (i.weight || 1), 0);
  const totalArea = width * height;
  // Map weight → area
  const sized = items.map(i => ({ ...i, _area: ((i.weight || 1) / totalWeight) * totalArea }));
  return squarifyRecurse(sized, [], x, y, width, height, []);
}

function squarifyRecurse(remaining, row, x, y, width, height, out) {
  if (!remaining.length) {
    layoutRow(row, x, y, width, height, out);
    return out;
  }
  const w = Math.min(width, height);
  const next = remaining[0];
  const newRow = [...row, next];
  if (row.length === 0 || worstRatio(newRow, w) <= worstRatio(row, w)) {
    return squarifyRecurse(remaining.slice(1), newRow, x, y, width, height, out);
  }
  // Commit current row, then recurse on remaining (including next)
  const newCoords = layoutRow(row, x, y, width, height, out);
  return squarifyRecurse(remaining, [], newCoords.x, newCoords.y, newCoords.w, newCoords.h, out);
}

function worstRatio(row, w) {
  if (!row.length) return Infinity;
  const sum = row.reduce((s, r) => s + r._area, 0);
  let min = Infinity, max = -Infinity;
  for (const r of row) {
    if (r._area < min) min = r._area;
    if (r._area > max) max = r._area;
  }
  const w2 = w * w;
  const s2 = sum * sum;
  return Math.max((w2 * max) / s2, s2 / (w2 * min));
}

function layoutRow(row, x, y, width, height, out) {
  if (!row.length) return { x, y, w: width, h: height };
  const sum = row.reduce((s, r) => s + r._area, 0);
  const isWider = width >= height;
  // The row fills the shorter side
  if (isWider) {
    // Row goes vertically on the left
    const rowWidth = sum / height;
    let offsetY = 0;
    for (const item of row) {
      const itemH = item._area / rowWidth;
      out.push({ ...item, x, y: y + offsetY, w: rowWidth, h: itemH });
      offsetY += itemH;
    }
    return { x: x + rowWidth, y, w: width - rowWidth, h: height };
  } else {
    // Row goes horizontally on the top
    const rowHeight = sum / width;
    let offsetX = 0;
    for (const item of row) {
      const itemW = item._area / rowHeight;
      out.push({ ...item, x: x + offsetX, y, w: itemW, h: rowHeight });
      offsetX += itemW;
    }
    return { x, y: y + rowHeight, w: width, h: height - rowHeight };
  }
}

/* ── Cell ── */
function Cell({ tile, quote, loading, isHovered, onHover, onClick }) {
  const price = quote?.currentPrice ?? null;
  const prev  = quote?.previousClose ?? null;
  const change = price != null && prev != null && prev !== 0
    ? ((price - prev) / prev) * 100
    : null;
  const isLoading = loading || !quote;
  const bg = isLoading ? 'var(--surface)' : changeBg(change);

  const area = tile.w * tile.h;
  const showLogo   = area > 7000 && !isLoading;
  const showChange = area > 2200 && !isLoading && change !== null;
  const showTicker = area > 600;
  const fontSize   = Math.min(28, Math.max(9, Math.sqrt(area) / 7));

  // Strip -USD and any numeric suffix Yahoo uses for disambiguating duplicate tickers
  const displayTicker = tile.displayTicker || tile.ticker.replace('-USD', '').replace(/\d+$/, '');
  const logoTicker = displayTicker.replace('-', '.');

  return (
    <button
      style={{
        position: 'absolute',
        left:   tile.x + 1,
        top:    tile.y + 1,
        width:  Math.max(0, tile.w - 2),
        height: Math.max(0, tile.h - 2),
        background: bg,
        color: '#fff',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        padding: '2px',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        gap: '2px',
        overflow: 'hidden',
        boxShadow: isHovered ? '0 0 0 2px rgba(255,255,255,0.7) inset' : 'none',
        animation: isLoading ? 'pulse 1.4s ease-in-out infinite' : 'none',
        transition: 'box-shadow 0.12s',
      }}
      onClick={onClick}
      onMouseEnter={() => onHover(tile.ticker)}
      onMouseLeave={() => onHover(null)}
      title={`${quote?.shortName || tile.name || tile.ticker}${change !== null ? ` · ${change.toFixed(2)}%` : ''}`}
    >
      {showLogo && (
        <img
          src={`https://img.logo.dev/ticker/${logoTicker}?token=pk_YJM_LolkQ9WfHMCBGBwlog&size=64`}
          alt=""
          style={{
            width: Math.min(40, fontSize * 1.6) + 'px',
            height: Math.min(40, fontSize * 1.6) + 'px',
            borderRadius: '6px', objectFit: 'contain',
            background: 'rgba(255,255,255,0.95)', padding: '3px',
          }}
          onError={e => { e.target.style.display = 'none'; }}
        />
      )}
      {showTicker && (
        <span style={{ fontWeight: 800, lineHeight: 1, fontSize: fontSize + 'px' }}>
          {displayTicker}
        </span>
      )}
      {showChange && (
        <span style={{ fontWeight: 700, lineHeight: 1, fontSize: Math.max(9, fontSize * 0.65) + 'px', opacity: 0.95 }}>
          {change >= 0 ? '+' : ''}{change.toFixed(2)}%
        </span>
      )}
    </button>
  );
}

function FilterChip({ active, onClick, children }) {
  return (
    <button onClick={onClick} style={{ ...s.chip, ...(active ? s.chipActive : {}) }}>
      {children}
    </button>
  );
}

/* ── Colour scale ── */
function changeBg(pct) {
  if (pct === null) return '#374151';
  if (pct >  4)    return '#14532d';
  if (pct >  2)    return '#166534';
  if (pct >  1)    return '#15803d';
  if (pct >  0.3)  return '#16a34a';
  if (pct > -0.3)  return '#475569';
  if (pct > -1)    return '#b91c1c';
  if (pct > -2)    return '#991b1b';
  if (pct > -4)    return '#7f1d1d';
  return '#450a0a';
}

const s = {
  filterBar: { display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '14px' },
  chip: {
    background: 'var(--surface)', border: '1px solid var(--border)',
    borderRadius: '999px', padding: '6px 12px', fontSize: '12px', fontWeight: 700,
    color: 'var(--text-secondary)', cursor: 'pointer', whiteSpace: 'nowrap',
    transition: 'all 0.15s',
  },
  chipActive: {
    background: '#4f46e5',          // indigo-600 — solid dark fill that works in both themes
    border: '1px solid #4338ca',    // indigo-700 rim
    color: '#fff',
  },
  canvas: {
    position: 'relative', width: '100%',
    background: 'var(--surface)',
    borderRadius: '8px', overflow: 'hidden',
  },
  legend: {
    display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap',
    marginTop: '12px', paddingTop: '12px', borderTop: '1px solid var(--border)',
    fontSize: '11px', color: 'var(--text-secondary)',
  },
  legendLabel: { fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px' },
  legendItem: { display: 'inline-flex', alignItems: 'center', gap: '6px' },
  legendSwatch: { width: '14px', height: '14px', borderRadius: '3px', display: 'inline-block' },
};
